import type { JSX } from 'react'
import type { Schema } from '@/api/client'
import { bucketsFor, isNumber, lastValue, pathOf, seriesFor } from './rank-series'

type SearchDashboard = Schema<'SearchDashboardOut'>

/**
 * The rank-movement chart as `design/proto/proto.js` draws it (parity P-SDASH-03,
 * P-SDASH-09): one line per tracked keyword on an inverted position axis, the
 * keyword's name written at the end of its line, and a data table of the same
 * figures behind *Show data table* (P-SDASH-08).
 *
 * **The x-axis is weeks, as the prototype labels it, except on the seven-day
 * range where a week is the whole window.** `SearchDashboardOut.keywords` is
 * daily; the chart averages each keyword's days into the window's weeks —
 * seven-day blocks from `start_date`, labelled *W1…* — because a 90-day line of
 * daily marks is a scribble and the prototype's twelve points are what a tenant
 * reads a trend from. Seven days are drawn as days.
 *
 * **Position 1 is at the top.** Plotting rank the natural way round shows every
 * improvement as a fall, which is the single easiest way to make this screen
 * lie to a tenant. The axis is #1 to #40 as drawn and grows by tens when a
 * keyword sits below it.
 *
 * A keyword's line breaks where a bucket has no reading — a day off the first
 * hundred is absent from the series, never a zero at the top of the chart.
 */
export function KeywordRankChart({ data }: { data: SearchDashboard }): JSX.Element {
  const buckets = bucketsFor(data)
  const series = seriesFor(data.keywords ?? [], buckets)
  const worst = Math.max(40, ...series.flatMap((line) => line.values.filter(isNumber)))
  const maxRank = Math.ceil(worst / 10) * 10 + 4
  const ticks = [
    1,
    ...Array.from({ length: Math.floor((maxRank - 4) / 10) }, (_, i) => (i + 1) * 10),
  ]

  const pad = { t: 18, r: 132, b: 30, l: 40 }
  const width = 720
  const height = 280
  const innerWidth = width - pad.l - pad.r
  const innerHeight = height - pad.t - pad.b
  const x = (index: number): number =>
    pad.l + (buckets.length > 1 ? (index / (buckets.length - 1)) * innerWidth : innerWidth / 2)
  const y = (rank: number): number => pad.t + ((rank - 1) / (maxRank - 1)) * innerHeight

  // Push overlapping end-labels apart: series that converge — which is what a
  // working SEO campaign looks like — would otherwise stack their names.
  const ends = series
    .map((line, index) => {
      const last = lastValue(line.values)
      return last === null ? null : { index, y: y(last.value) + 4, name: line.name }
    })
    .filter((end): end is { index: number; y: number; name: string } => end !== null)
    .sort((a, b) => a.y - b.y)
  for (let i = 1; i < ends.length; i += 1) {
    const prev = ends[i - 1]
    const here = ends[i]
    if (prev !== undefined && here !== undefined && here.y - prev.y < 17) here.y = prev.y + 17
  }
  const labelY = new Map(ends.map((end) => [end.index, end.y]))

  const summary = `Average Google position over ${String(buckets.length)} ${
    buckets.unit
  }${buckets.length === 1 ? '' : 's'} for ${series.map((line) => line.name).join(', ')}.`

  return (
    <svg
      className="app-rank-chart app-rank-chart--keywords"
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      role="img"
      aria-label={summary}
    >
      {ticks.map((rank) => (
        <g key={rank}>
          <line
            className="app-rank-chart__grid"
            x1={pad.l}
            x2={pad.l + innerWidth}
            y1={y(rank)}
            y2={y(rank)}
          />
          <text className="app-rank-chart__tick" x={pad.l - 10} y={y(rank) + 4} textAnchor="end">
            #{rank}
          </text>
        </g>
      ))}
      {buckets.labels.map((label, index) =>
        index % 2 === 1 && buckets.length > 6 ? null : (
          <text
            key={label}
            className="app-rank-chart__tick"
            x={x(index)}
            y={height - 10}
            textAnchor="middle"
          >
            {label}
          </text>
        ),
      )}
      {series.map((line, index) => {
        const tone = `app-rank-chart__series--${String((index % 3) + 1)}`
        const last = lastValue(line.values)
        return (
          <g key={line.name} className={tone}>
            <path className="app-rank-chart__line" d={pathOf(line.values, x, y)} />
            {last === null ? null : (
              <>
                <circle
                  className="app-rank-chart__point"
                  cx={x(last.index)}
                  cy={y(last.value)}
                  r={4}
                />
                <text
                  className="app-rank-chart__label"
                  x={x(last.index) + 12}
                  y={labelY.get(index) ?? y(last.value) + 4}
                >
                  {line.name.length > 20 ? `${line.name.slice(0, 19)}…` : line.name}
                  <title>{line.name}</title>
                </text>
              </>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/** The chart's figures as a table — one row per keyword, one column per bucket. */
export function KeywordRankTable({ data }: { data: SearchDashboard }): JSX.Element {
  const buckets = bucketsFor(data)
  const series = seriesFor(data.keywords ?? [], buckets)
  return (
    <div className="ar-table-wrap" tabIndex={0}>
      <table className="ar-table app-rank-table">
        <caption className="ar-muted">Average Google position by {buckets.unit}</caption>
        <thead>
          <tr>
            <th scope="col">Keyword</th>
            {buckets.labels.map((label) => (
              <th scope="col" className="ar-num" key={label}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((line, index) => (
            <tr key={line.name}>
              <th scope="row">
                <span
                  className={`app-rank-table__swatch app-rank-chart__series--${String((index % 3) + 1)}`}
                  aria-hidden="true"
                />
                {line.name}
              </th>
              {line.values.map((value, column) => (
                <td className="ar-num" key={buckets.labels[column]}>
                  {value === null ? '—' : value.toFixed(1)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
